<template>
    <div class="flex flex-col gap-4">
        <message-hub-card-stack :count="selectedThreads.size" @clear="handleClearSelection">
            <template v-slot:title>
                <span class="text-lg font-extrabold"> {{ selectedThreads.size }} Total </span>
            </template>

            <vx-box color="info">
                <div class="p-6">
                    <div class="rounded-xl overflow-hidden bg-white border border-solid border-slate-200 flex">
                        <button
                            v-for="action in ouGlobalActions"
                            v-show="action.show"
                            type="button"
                            :disabled="action.loading || selectedThreads.size === 0"
                            :key="action.id"
                            @click="action.handler"
                            :data-test="`${action.id}-button`"
                            class="appearance-none text-slate-800 bg-transparent flex-1 gap-1 flex flex-col items-center justify-center py-3.5 px-4 border-r border-solid border-slate-200 border-l-0 border-y-0 hover:bg-slate-50 active:bg-slate-100 last:border-0"
                            :class="{
                                'opacity-50 cursor-default': action.loading,
                                'cursor-pointer': !action.loading,
                            }"
                        >
                            <font-awesome-icon
                                widthAuto
                                v-if="!action.loading"
                                :icon="action.icon"
                                :class="[action.color, 'text-sm h-3.5']"
                            />
                            <vx-icon-loading v-else spin class="w-3.5!" />
                            <span class="text-[10px] leading-none text-slate-500 font-semibold">
                                {{ action.label }}
                            </span>
                        </button>
                    </div>
                </div>
            </vx-box>
        </message-hub-card-stack>

        <message-hub-bulk-actions-by-group
            v-for="[groupId, threads] in selectedThreadsByGroup"
            :key="groupId"
            :groupId="groupId"
            :threads="threads"
            @openSnippetModal="openSnippetModal"
            @openAssignModal="openAssignModal"
        />
    </div>

    <message-builder-snippets v-model:visible="snippetModalVisible" @selected="handleSnippetSelected" />
    <message-hub-assign-modal
        v-model:visible="assignModalVisible"
        emitOnly
        :groupId="assignTargetGroupId"
        @submit="handleAssignSubmit"
    />
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxIconLoading } from '@voxie/frontend-components';
import MessageHubBulkActionsByGroup from './MessageHubBulkActionsByGroup.vue';
import MessageHubCardStack from './MessageHubCardStack.vue';
import MessageBuilderSnippets from '~/components/general/message-builder/modals/MessageBuilderSnippets.vue';
import MessageHubAssignModal from '../MessageHubAssignModal.vue';
import { useMessageHub } from '../useMessageHub';
import { useMessageHubBulkActions } from './useMessageHubBulkActions';
import { useResponsiveLayout } from '~/composables/useResponsiveLayout';
import type { Thread } from '~/services/threads/threadsClient.types';

const route = useRoute();
const router = useRouter();
const { isMd } = useResponsiveLayout();
const { selectedThreads, selectedThreadsByGroup, clearSelection } = useMessageHub();

const { actions, closeWithSnippet, handleBulkAssign } = useMessageHubBulkActions();

const ouGlobalActions = computed(() =>
    actions.value.filter((action) => action.id !== 'close-with-snippet' && action.id !== 'assign')
);

const handleClearSelection = () => {
    clearSelection();
    if (!isMd.value) {
        router.push({ name: 'message-hub', query: route.query });
    }
};

const snippetModalVisible = ref(false);
const assignModalVisible = ref(false);
const snippetTargetThreads = ref<Thread[]>([]);
const assignTargetThreads = ref<Thread[]>([]);
const assignTargetGroupId = ref<string | undefined>();

const openSnippetModal = (threads: Thread[]) => {
    snippetTargetThreads.value = threads;
    snippetModalVisible.value = true;
};

const handleSnippetSelected = (snippetData: {
    body: string;
    media_url: string | null;
    media_content_type: string | null;
}) => {
    closeWithSnippet(snippetData, {}, snippetTargetThreads.value);
    snippetModalVisible.value = false;
};

const openAssignModal = (threads: Thread[]) => {
    assignTargetThreads.value = threads;
    assignTargetGroupId.value = threads[0]?.group_id ? String(threads[0].group_id) : undefined;
    assignModalVisible.value = true;
};

const handleAssignSubmit = (assigneeId: number | string) => {
    handleBulkAssign(assigneeId, assignTargetThreads.value);
    assignModalVisible.value = false;
};
</script>
